/**
 * TypeScript types matching backend Pydantic models.
 */

export type NodeType = 'king' | 'counter_king' | 'gatekeeper' | 'hedge' | 'regular';
export type ExposureType = 'gex' | 'vex';

export interface Node {
  strike: number;
  value: number;
  abs_value: number;
  node_type: NodeType;
  exposure_type: ExposureType;
  is_positive: boolean;
  rate_of_change: number | null;
  touch_count: number;
}

export interface AirPocket {
  low_strike: number;
  high_strike: number;
  exposure_type: ExposureType;
}

export interface DailyColumn {
  date: string;
  gex_by_strike: Record<string, number>;
  vex_by_strike: Record<string, number>;
  spot_price: number;
  spot_high: number;
  spot_low: number;
  update_count: number;
}

// ── Multi-horizon analytics ─────────────────────────────
//
// Mirrors backend `engine.analytics.FrameAnalytics`. Splits the regime
// read across FRONT (front-week) / NEAR (within 14 days) / FULL (every
// expiry) / STRUCTURAL (full + directional narrative). Magnets are the
// significant long-gamma walls (positive GEX, dealer pin); amplifiers
// are the short-gamma cascade zones (negative GEX). `fw_*` variants
// restrict to front-week so traders can see the near-term picture
// without the structural longs masking it.

export interface StrikeRef {
  strike: number;
  value: number;
}

export interface HorizonStats {
  label: 'FRONT' | 'NEAR' | 'FULL' | 'STRUCTURAL' | string;
  regime: string;
  net: number;
  max_abs: number;
  flip: number | null;
  flip_distance_pct: number | null;
}

export interface FrameAnalytics {
  front: HorizonStats;
  near: HorizonStats;
  full: HorizonStats;
  structural: HorizonStats;
  structural_note: string;

  magnet_above: StrikeRef | null;
  magnet_below: StrikeRef | null;
  fw_magnet_above: StrikeRef | null;
  fw_magnet_below: StrikeRef | null;

  amp_above: StrikeRef | null;
  amp_below: StrikeRef | null;
  fw_amp_above: StrikeRef | null;
  fw_amp_below: StrikeRef | null;

  strike_sums_gex: number[];
  strike_sums_vex: number[];
}

// Frame as it arrives from the WebSocket. Several fields are stripped
// server-side via api/ws.py::slim_for_wire() to keep WS payload small —
// they're marked optional here. The full frame is still available via
// /api/snapshot/{ticker} REST when needed (Dashboard, scanner replay).
//
// Stripped from WS, reachable via REST:
//   • daily_columns       → /api/daily/{ticker}
//   • gex_by_strike,      → derived client-side from gex_by_expiration
//     vex_by_strike       → derived client-side from vex_by_expiration
//   • vex_air_pockets     → never read by frontend
//   • data_provider       → operational metadata, no UI use
//   • REGULAR-typed nodes → filtered out at the wire boundary
export interface HeatmapFrame {
  ticker: string;
  spot_price: number;
  timestamp: string;
  // Strike-aggregate dicts — present in REST responses, omitted from WS
  // since the heatmap derives strike axis from `strikes` and per-cell
  // values from gex_by_expiration directly.
  gex_by_strike?: Record<string, number>;
  vex_by_strike?: Record<string, number>;
  gex_nodes: Node[];
  vex_nodes: Node[];
  gex_air_pockets: AirPocket[];
  vex_air_pockets?: AirPocket[];
  gex_flip_point: number | null;
  daily_columns?: DailyColumn[];
  gex_by_expiration: Record<string, Record<string, number>>;
  vex_by_expiration: Record<string, Record<string, number>>;
  strikes: number[];
  expirations_included: string[];
  data_provider?: string;
  is_market_open: boolean;
  analytics?: FrameAnalytics | null;
}

export type TickerCategory = 'equity';

export interface TickerStatus {
  ticker: string;
  // Always 'equity' (SPY/SPX/QQQ/NDX/IWM and open-universe symbols).
  // Sent by the backend; the UI no longer splits the pill row by
  // category, but we keep the field so server payloads parse cleanly.
  category?: TickerCategory;
  has_data: boolean;
  spot_price: number | null;
  last_update: string | null;
  is_market_open: boolean | null;
  gex_node_count: number;
  vex_node_count: number;
}

export type ViewMode = 'gex' | 'vex';

export interface WebSocketMessage {
  type?: string;
  ticker?: string;
  timestamp?: string;
}

// ── Auth ─────────────────────────────────────────────────

// Tier mirrors backend users.Tier. Renders directly to UI labels in
// the upgrade flow; ordering matters (TIER_ORDER in users.py) so the
// frontend can compare "user has access to feature X minimum tier Y."
export type Tier = 'free' | 'essentials' | 'pro';
export const TIER_ORDER: readonly Tier[] = ['free', 'essentials', 'pro'] as const;

/** Returns true iff `userTier` is at or above `minimum`. Admin overrides
 *  this check at call sites — see useAuth's `hasTier`. */
export function tierAtLeast(userTier: Tier, minimum: Tier): boolean {
  return TIER_ORDER.indexOf(userTier) >= TIER_ORDER.indexOf(minimum);
}

export interface AuthState {
  authed: boolean;
  username: string | null;    // email (new flow) or auth_username (legacy basic)
  isAdmin: boolean;
  tier: Tier;
  userId: number | null;
}

export interface MeResponse {
  authed: boolean;
  /** Numeric user id (JWT/cookie flow). Absent on the legacy path. */
  id?: number;
  username: string;
  is_admin?: boolean;
  tier?: Tier;
  // Where the auth password is sourced from on this deployment:
  // 'env' (AUTH_PASSWORD locked at startup, can't rotate via UI) or
  // 'file' (writable secret in the persistent volume). The Settings
  // Account tab hides the change-password form when this is 'env'.
  auth_source?: 'env' | 'file';
  // True iff auth_source == 'file' AND the secret-file path is
  // writable in the current runtime. Frontend uses this directly so
  // it doesn't have to re-implement the writability check.
  can_change_password?: boolean;
}

/** Shape of the JWT payload issued by /api/auth/signup + /login. */
export interface JwtPayload {
  sub: string;       // user_id as string (JWT spec wants string)
  email: string;
  tier: Tier;
  is_admin: boolean;
  iat: number;
  exp: number;
}

/** Response from /api/auth/signup and /api/auth/login. */
export interface AuthResponse {
  token: string;
  token_type: 'bearer';
  expires_in: number;
  user: {
    id: number;
    email: string;
    tier: Tier;
    is_admin: boolean;
  };
}

// ── Visual settings ─────────────────────────────────────

// The single color axis. `cobalt` (Cobalt Tape — institutional navy +
// ice-blue) is the default; `plasma` (violet → magenta) is the
// user-selectable alternate. Both share the type system, layout, logo,
// and copy — the swap is color-only (see styles/tokens.css).
export type Theme = 'cobalt' | 'plasma';
export type Fontset = 'sf' | 'jetbrains';
export type Density = 'comfortable' | 'compact';
export type Motion = 'auto' | 'on' | 'off';

export interface VisualSettings {
  theme: Theme;
  fontset: Fontset;
  density: Density;
  motion: Motion;
}

// Historical replay was sunset (see docs/REPLAY_PLAN.md). The replay
// frame/extent/controls types lived here and were removed with the
// feature; reintroduce them with the Option C rebuild.

// ── Regime / King API responses ─────────────────────────

export type Regime = 'POSITIVE_GAMMA' | 'NEGATIVE_GAMMA' | 'UNKNOWN';

export interface RegimeResponse {
  ticker: string;
  regime: Regime;
  spot_price: number | null;
  flip_point: number | null;
}

export interface KingResponse {
  ticker: string;
  strike: number | null;
  value: number | null;
  distance_pct: number | null;
}

// ── Notable Flow feed ───────────────────────────────────
//
// One detected options-flow signal as served by GET /api/flow. The
// backend's sweep flags unusual single-leg activity (high vol/OI,
// large notional) and tags a directional `bias`. The feed renders
// these newest-first as a chatbox-style stream on the Scanner page.
// `bias` drives the line emoji/arrow and subtle row tint.

export type FlowBias = 'bullish' | 'bearish' | 'neutral';
export type FlowOptionType = 'call' | 'put';

export interface FlowSignal {
  ticker: string;
  option_type: FlowOptionType;
  strike: number;
  /** ISO date (YYYY-MM-DD) of the contract expiration. */
  expiration: string;
  volume: number;
  open_interest: number;
  vol_oi_ratio: number;
  /** Option mid price. */
  mid: number;
  /** Premium notional in dollars (volume × mid × 100). */
  notional: number;
  bias: FlowBias;
  /** Underlying spot at detection time. */
  spot: number;
  /** Normalised 0–1 conviction score the sweep assigns. */
  score: number;
  /** ISO-8601 UTC timestamp when the sweep first flagged this. */
  detected_at: string;
  /** Pre-rendered one-line human summary (fallback display text). */
  summary: string;
}

export interface FlowResponse {
  /** Newest-first. */
  flows: FlowSignal[];
  /** ISO-8601 UTC timestamp of this sweep's data cut. */
  as_of: string;
  /** Tickers currently in the user's watchlist (scope of a global feed). */
  watchlist: string[];
  /** Seconds until the next server-side sweep is expected. */
  next_sweep_eta_seconds: number;
}
